'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { XMarkIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { scamAlertsApi } from '@/lib/api';

interface ReportFormData {
  projectName: string;
  alertType: string;
  description: string;
  estimatedDamage: string;
  evidenceUrl: string;
  anonymous: boolean;
}

export default function ReportScamForm({ onClose }: { onClose: () => void }) {
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ReportFormData>();

  const onSubmit = async (data: ReportFormData) => {
    setSubmitting(true);
    try {
      const { error } = await scamAlertsApi.submit({
        project_name: data.projectName,
        alert_type: data.alertType,
        severity: 'Medium',
        report_status: 'Under Investigation',
        description: data.description,
        estimated_damage: data.estimatedDamage || null,
        evidence_url: data.evidenceUrl || null,
        anonymous: data.anonymous,
        reporter_count: 1,
        verified: false,
      });

      if (error) {
        toast.error('Failed to submit report. Please try again.');
        console.log('Scam report error:', error);
      } else {
        reset();
        toast.success('Scam report submitted — under review within 24 hours');
        onClose();
      }
    } catch (err: any) {
      toast.error('An unexpected error occurred.');
      console.log('Submit error:', err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-card border border-negative/20 rounded-xl p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">Report a Scam Project</h3>
        <button
          onClick={onClose}
          className="p-1 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
        >
          <XMarkIcon style={{ width: 16, height: 16 }} />
        </button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">
            Project / Platform Name *
          </label>
          <input
            {...register('projectName', { required: 'Project name is required' })}
            placeholder="e.g. MetaEarn Pro"
            className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          {errors.projectName && (
            <p className="text-negative text-xs mt-1">{errors.projectName.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">Alert Type *</label>
          <select
            {...register('alertType', { required: 'Select an alert type' })}
            className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="">Select type...</option>
            <option>Ponzi Scheme</option>
            <option>Rug Pull</option>
            <option>Phishing</option>
            <option>Exit Scam</option>
            <option>Fake Exchange</option>
            <option>MLM Fraud</option>
          </select>
          {errors.alertType && (
            <p className="text-negative text-xs mt-1">{errors.alertType.message}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-foreground mb-1">
            Description *
          </label>
          <textarea
            {...register('description', { required: 'Description is required', minLength: { value: 30, message: 'Minimum 30 characters' } })}
            rows={3}
            placeholder="Describe what happened, how you discovered this, and any evidence..."
            className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
          />
          {errors.description && (
            <p className="text-negative text-xs mt-1">{errors.description.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              Est. Damage (USD)
            </label>
            <input
              {...register('estimatedDamage')}
              placeholder="e.g. $5,000"
              className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1">
              Evidence URL
            </label>
            <input
              {...register('evidenceUrl')}
              placeholder="https://..."
              type="url"
              className="w-full bg-input border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('anonymous')}
            className="w-4 h-4 accent-primary rounded"
          />
          <span className="text-xs text-muted-foreground">Submit anonymously</span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-lg bg-negative/20 border border-negative/30 text-negative font-semibold text-sm hover:bg-negative/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-95"
        >
          {submitting ? (
            <>
              <span className="w-4 h-4 border-2 border-negative/30 border-t-negative rounded-full animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <PaperAirplaneIcon style={{ width: 14, height: 14 }} />
              Submit Report
            </>
          )}
        </button>
      </form>
    </div>
  );
}